import React from 'react'
import {useSelector} from 'react-redux'
import {CacheProvider} from '@emotion/react'
import {ErrorBoundary} from 'components/ErrorBoundary'
import InitializeABTesting from 'components/InitializeABTesting'
import InitializeMeta from 'components/InitializeMeta'
import {usePageProps} from 'context/unsafePageProps'
import {
  getParametrizedHotelContext,
  getSearchContext
} from 'middleware/analytics/selectors'
import {
  getAnonymousId,
  getDeviceLayout,
  getIsRtlLanguage,
  getIsSyntheticTest,
  getLanguageCode,
  getUserCountryCode
} from 'modules/meta/selectors'
import * as Opticks from 'opticks'

import {
  DeviceLayoutProvider,
  deviceTypeToBreakpoint
} from '@vio/atlas/context/deviceLayout'
import FontsLoaderNext from '@vio/atlas/src/components/designSystem/helpers/FontsLoaderNext/FontsLoaderNext'
import {ToastProvider} from '@vio/atlas/src/context/toastController'
import {RouterProvider} from '@vio/core/src/_web/router/context/RouterProviderNext'
import {evaluateInRuntime} from '@vio/core/src/_web/utils/css/emotion-rtl'
import DummyOpticksProvider from '@vio/core/src/experiments/components/DummyOpticksProvider'
import OpticksProvider, {
  OpticksContext
} from '@vio/core/src/experiments/components/OpticksProvider'
import ConfigureI18n from '@vio/core/src/localization/components/ConfigureI18n'
import {getIsoptimizelyDisabled} from '@vio/debug-panel'
import {SusiProvider} from '@vio/shared/src/authentication/SUSI/bottomSheet/SusiBottomSheet/SusiProvider'
import {PriceWatchToastListener} from '@vio/shared/src/priceWatch/components/PriceWatchToastListener'

/**
 * Toggling in the server side is not supported for now. We use a dummy provider that will
 * always return the A-side variant.
 */
const UniversalOpticksProvider = ({children}: {children: JSX.Element}) => {
  const anonymousId = useSelector(getAnonymousId)
  const isSyntheticTest = useSelector(getIsSyntheticTest)
  if (
    typeof window === 'undefined' ||
    isSyntheticTest // Disable opticks for website synthetics
  ) {
    return (
      <DummyOpticksProvider userId={anonymousId ?? 'search'}>
        <OpticksContext.Provider value={Opticks}>
          {children}
        </OpticksContext.Provider>
      </DummyOpticksProvider>
    )
  }

  const forceDisableOptimizelyLog = getIsoptimizelyDisabled()

  return (
    <OpticksProvider
      // Intentional to prevent opticks from fetching the datafile on the client.
      dataFileUrl={undefined}
      disableOptimizelyLogs={forceDisableOptimizelyLog}
    >
      {children}
    </OpticksProvider>
  )
}

const AppServerProviders: React.FC = ({children}) => {
  const {preloadedMessages, brand, experiments} = usePageProps()
  const publicUrl = process.env.NEXT_PUBLIC_ASSETS_ENDPOINT
  const languageCode = useSelector(getLanguageCode)
  const userCountryCode = useSelector(getUserCountryCode)
  const isRtlLanguage = useSelector(getIsRtlLanguage)

  const anonymousId = useSelector(getAnonymousId)
  const deviceLayout = useSelector(getDeviceLayout)

  return (
    <UniversalOpticksProvider>
      <InitializeABTesting>
        <InitializeMeta>
          <ConfigureI18n
            languageCode={languageCode}
            publicUrl={publicUrl}
            countryCode={userCountryCode}
            preloadedMessages={preloadedMessages}
          >
            <CacheProvider value={evaluateInRuntime(isRtlLanguage)}>
              <ErrorBoundary>
                <RouterProvider>
                  <ToastProvider>
                    <DeviceLayoutProvider
                      updateOnMount
                      value={deviceTypeToBreakpoint(deviceLayout)}
                    >
                      <SusiProvider
                        brand={brand}
                        userCountryCode={userCountryCode}
                        anonymousId={anonymousId}
                      >
                        <FontsLoaderNext
                          languageCode={languageCode}
                          isBSideNonLatinFontsExperiment={
                            experiments['319d377e-non-latin-fonts'] === 'b'
                          }
                        />
                        <PriceWatchToastListener
                          getSearchContext={getSearchContext}
                          getParametrizedHotelContext={
                            getParametrizedHotelContext
                          }
                        />
                        {children}
                      </SusiProvider>
                    </DeviceLayoutProvider>
                  </ToastProvider>
                </RouterProvider>
              </ErrorBoundary>
            </CacheProvider>
          </ConfigureI18n>
        </InitializeMeta>
      </InitializeABTesting>
    </UniversalOpticksProvider>
  )
}

export default AppServerProviders
